import { FormEvent, useEffect, useRef, useState } from 'react'

import { Spinner } from '@/components/spinner/spinner'
import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { fontStyles } from '@/components/typography/typographyUtils'
import { cn } from '@/libs/tw'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { translate } from '@/utils/translator'

import { usePromotion } from '../hooks/usePromotion'
import { PromotionModal } from './promotionModal'

export const PromotionCodeBanner = () => {
	const setPromotions = useCartStore.use.setPromotions()
	const urlQuery = useAppConfigStore.use.urlQuery()

	const [value, setValue] = useState('')
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [hasError, setHesError] = useState(false)
	const [promotionCode, setPromotionCode] = useState<string>()
	const { data: promotions, isFetching } = usePromotion(promotionCode)

	const isValid = promotions && Boolean(promotions.length)

	const hasRun = useRef(false)

	useEffect(() => {
		if (hasRun.current) return
		if (!urlQuery) return

		const { promotionCode } = urlQuery

		setPromotionCode(promotionCode ?? undefined)
		setValue(promotionCode ?? '')
		hasRun.current = true
	}, [urlQuery])

	useEffect(() => {
		setHesError(false)
		if (!promotions) return

		if (promotions.length === 0) {
			setPromotions([])
			return setHesError(true)
		}

		setPromotions(promotions, promotionCode)
	}, [promotionCode, promotions, setPromotions])

	useEffect(() => {
		if (value.length === 0) {
			setHesError(false)
		}
	}, [value.length])

	const handleClick = () => {
		if (!value) return

		if (value.length === 0) return setPromotionCode(undefined)

		setPromotionCode(value)
	}

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		handleClick()
	}

	return (
		<>
			<PromotionModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				promotionCode={promotionCode}
			/>
			<div className="flex w-full flex-col gap-16 bg-naturel md:w-[724px] md:rounded-promotion md:p-24 md:px-32">
				<div className="flex flex-col justify-between text-promotion-textColor md:flex-row">
					<Typography element="h2" style="p-ms-bold">
						{translate('promotion-instruction')}
					</Typography>
				</div>
				<div className="flex w-full flex-col gap-8 md:grid md:grid-cols-[58%_1fr] md:gap-0">
					<div className="w-full">
						<form onSubmit={onSubmit} className={cn('relative')}>
							<input
								className={cn(
									fontStyles['p'],
									'h-[50px] w-full rounded-8 border border-solid border-promotion-accentColor p-16  pr-48 text-promotion-inputText placeholder:text-promotion-inputText ',
									'md:border-r-none md:rounded-l-8 md:rounded-r-none',
									hasError && 'border-danger text-danger',
									isValid && 'text-promotion-inputText'
								)}
								value={value}
								onChange={(e) => setValue(e.target.value)}
								data-testid="pc-input"
								type="text"
								placeholder={translate('promotion-placeholder')}
							/>
							{hasError && (
								<SvgIcon
									collectionName="ibe"
									iconName="cross"
									className={cn(
										'absolute right-16 top-1/2 h-24 w-24 -translate-y-1/2 text-danger'
									)}
								/>
							)}
							{isValid && (
								<SvgIcon
									collectionName="ibe"
									iconName="check"
									className={cn(
										'absolute right-16 top-1/2 h-24 w-24 -translate-y-1/2 text-promotion-successIcon'
									)}
								/>
							)}
						</form>
					</div>
					<button
						data-testid="pc-check"
						className="h-[50px] cursor-pointer rounded-8 bg-promotion-button uppercase text-promotion-buttonText md:rounded-l-none"
						onClick={handleClick}
					>
						{isFetching ? (
							<Spinner />
						) : (
							<Typography style="p-ms-bold">
								{translate('promotion-button-label')}
							</Typography>
						)}
					</button>
				</div>
				{hasError && (
					<div className="flex items-start gap-10" data-testid="pc-error-msg">
						<div className={cn('pt-2')}>
							<SvgIcon
								collectionName="ibe"
								iconName="info"
								className="h-16 w-16 text-danger"
							/>
						</div>
						<Typography style="p" element="p" className="text-promotion-accentColor">
							{translate('promotion-error')}
						</Typography>
					</div>
				)}
				{promotions && Boolean(promotions.length) && (
					<button
						className="flex items-start gap-10"
						data-testid="pc-valid"
						onClick={() => setIsModalOpen(true)}
					>
						<div className={cn('pt-2')}>
							<SvgIcon
								collectionName="ibe"
								iconName="info"
								className="h-16 w-16 text-promotion-textColor"
							/>
						</div>
						<Typography
							style="p"
							element="p"
							className="text-promotion-textColor underline"
						>
							{translate('promotion-more-info')}
						</Typography>
					</button>
				)}
			</div>
		</>
	)
}
